/**
 * Sistema de Níveis de Log
 * 
 * Controla a verbosidade dos logs através de níveis configuráveis (1-3):
 * - Nível 1: Mínimo (apenas erros críticos e operações críticas)
 * - Nível 2: Operacional (default - operações importantes sem JSON completo)
 * - Nível 3: Debug completo (tudo com JSON completo)
 */

/**
 * Níveis de log disponíveis
 */
export enum LogLevel {
  MINIMAL = 1,      // Apenas erros críticos e operações críticas
  OPERATIONAL = 2,  // Operações importantes sem JSON completo (default)
  DEBUG = 3         // Tudo com JSON completo
}

/**
 * Categorias de log para filtragem
 */
export enum LogCategory {
  HEALTH = 'health',           // health:pong, conexões de health check
  AUTH = 'auth',              // HMAC, SessionToken, detalhes de autenticação
  PAYLOAD = 'payload',        // JSON completo de payloads
  CONNECTION = 'connection',  // Logs de conexão CorelDRAW
  BUSINESS = 'business',      // Operações de negócio (ORDER, PROD, SNAPSHOT)
  API = 'api',                // Logs de API
  ERROR = 'error',            // Erros (sempre logado)
  QUEUE = 'queue',            // Logs de fila de mensagens
  DEV = 'dev',                // Logs de desenvolvimento/mocks
  EVENT = 'event'             // Logs de eventos C# → Frontend
}

/**
 * Obtém o nível de log configurado via variável de ambiente
 * Default: 2 (OPERATIONAL)
 */
export function getLogLevel(): LogLevel {
  const envLevel = import.meta.env.VITE_LOG_LEVEL;
  
  if (!envLevel) {
    return LogLevel.OPERATIONAL; // Default
  }
  
  const level = Number.parseInt(envLevel, 10);
  
  if (isNaN(level) || level < 1 || level > 3) {
    console.warn(`Nível de log inválido: ${envLevel}. Usando nível 2 (OPERATIONAL) como padrão.`);
    return LogLevel.OPERATIONAL;
  }
  
  return level as LogLevel;
}

/**
 * Verifica se uma categoria de log deve ser registrada no nível atual
 */
export function shouldLog(category: LogCategory, level: LogLevel = getLogLevel()): boolean {
  // Erros sempre são logados
  if (category === LogCategory.ERROR) {
    return true;
  }
  
  // Nível 1: Apenas erros, operações críticas de negócio e eventos (sem payload completo)
  if (level === LogLevel.MINIMAL) {
    return category === LogCategory.BUSINESS || category === LogCategory.EVENT; // Operações críticas + eventos
  }
  
  // Nível 2: Todas as categorias exceto HEALTH, AUTH, PAYLOAD, DEV
  if (level === LogLevel.OPERATIONAL) {
    // HEALTH sempre filtrado
    if (category === LogCategory.HEALTH) {
      return false;
    }
    // AUTH, PAYLOAD e DEV não devem aparecer no nível 2 (apenas no nível 3)
    if (category === LogCategory.AUTH || category === LogCategory.PAYLOAD || category === LogCategory.DEV) {
      return false;
    }
    return true;
  }
  
  // Nível 3: Todas as categorias (exceto HEALTH que continua filtrado)
  if (level === LogLevel.DEBUG) {
    // HEALTH continua filtrado mesmo no nível 3
    if (category === LogCategory.HEALTH) {
      return false;
    }
    return true;
  }
  
  return false;
}

/**
 * Verifica se JSON completo deve ser incluído no log
 * Nível 2: JSON organizado (campos principais)
 * Nível 3: JSON completo
 */
export function shouldIncludeJson(level: LogLevel = getLogLevel()): boolean {
  // Nível 2 e 3 incluem JSON (nível 2 organizado, nível 3 completo)
  return level >= LogLevel.OPERATIONAL;
}

/**
 * Verifica se deve incluir JSON completo (nível 3) ou organizado (nível 2)
 */
export function shouldIncludeFullJson(level: LogLevel = getLogLevel()): boolean {
  // Apenas nível 3 inclui JSON completo
  return level === LogLevel.DEBUG;
}

/**
 * Verifica se detalhes de autenticação devem ser logados
 */
export function shouldLogAuthDetails(level: LogLevel = getLogLevel()): boolean {
  // Apenas nível 3 loga detalhes de autenticação
  return level === LogLevel.DEBUG;
}

/**
 * Verifica se detalhes de conexão devem ser logados
 */
export function shouldLogConnectionDetails(level: LogLevel = getLogLevel()): boolean {
  // Nível 1-2: apenas sucesso/erro (sem detalhes)
  // Nível 3: todos os detalhes
  return level === LogLevel.DEBUG;
}

/**
 * Cria um resumo de payload organizado para nível 2
 * Inclui campos principais de forma organizada
 */
export function createPayloadSummary(data: any): any {
  if (!data || typeof data !== 'object') {
    return data;
  }
  
  // Extrair informações principais
  const summary: any = {};
  
  if (data.action) {
    summary.action = data.action;
  }
  
  if (data.requestId) {
    summary.requestId = data.requestId;
  }
  
  if (data.status) {
    summary.status = data.status;
  }
  
  if (data.message) {
    summary.message = data.message;
  }
  
  // Para payloads de mensagens C# - organizar campos principais
  if (data.data || data.payload) {
    const payload = data.data || data.payload || {};
    const organized: any = {};
    
    // Campos principais organizados
    if (payload.comando) organized.comando = payload.comando;
    if (payload.caminhoArquivo) organized.caminhoArquivo = payload.caminhoArquivo;
    if (payload.timestamp) organized.timestamp = payload.timestamp;
    if (payload.apiOnline !== undefined) organized.apiOnline = payload.apiOnline;
    
    if (Object.keys(organized).length > 0) {
      summary.payload = organized;
    }
  }
  
  // Adicionar tamanho se disponível
  if (data.payloadSize) {
    summary.payloadSize = data.payloadSize;
  }
  
  return summary;
}

/**
 * Formata mensagem de log conforme o nível configurado
 */
export function formatLogMessage(
  category: LogCategory,
  message: string,
  data?: any,
  level: LogLevel = getLogLevel()
): { message: string; data?: any } {
  // Se não deve logar, retorna vazio (será filtrado pelo caller)
  if (!shouldLog(category, level)) {
    return { message: '', data: undefined };
  }
  
  // Nível 1: Apenas mensagem, sem dados
  if (level === LogLevel.MINIMAL) {
    return { message, data: undefined };
  }
  
  // Nível 2: Mensagem + resumo de dados (sem JSON completo)
  if (level === LogLevel.OPERATIONAL) {
    if (category === LogCategory.PAYLOAD && data) {
      return { message, data: createPayloadSummary(data) };
    }
    // Para outras categorias, manter dados mas sem detalhes profundos
    return { message, data };
  }
  
  // Nível 3: Mensagem + dados completos
  return { message, data };
}

/**
 * Verifica se está em modo de desenvolvimento (logs só funcionam em dev)
 */
export function isDevelopmentMode(): boolean {
  try {
    const mode = (import.meta as any)?.env?.MODE;
    const isDev = mode !== 'production';
    const debugEnabled = (window as any)?.__DEBUG_LOGS__ !== false;
    return isDev && debugEnabled;
  } catch {
    return false;
  }
}

